import type { Config } from "@puckeditor/core";
import {browserStyle, browserTextStyle, paragraphPreview, templateTextPreview} from "./fields";
import {InputInspector} from "./inputInspector";
import {InputPreview} from "./inputPreview";
import {ParagraphInspector, TemplateStringInspector} from "./paragraphInspector";
import { generatedInputId, inputKindForBlock } from "./state";
import { writeStyle } from "./serialization";
import { StyleInspector } from "./styleInspector";
import type { BlockStyle, InputKind, TemplateComponents } from "./types";
import { automaticKeys, sampleValueFor } from "./variables";
import { useTemplateBuilder } from "./context";

type InputComponentConfig = Config<TemplateComponents>["components"]["TextInput"];
export const templateBuilderRootAllowedBlocks = ["Page"];
export const templateBuilderRootZone = "default-zone";

const styleField = {
  type: "custom" as const,
  label: "Style configuration",
  render: ({ value, onChange }: { value?: BlockStyle | string; onChange: (value?: string) => void }) =>
    <StyleInspector value={value} onChange={onChange} />,
};

const inputFields = (kind: InputKind) => ({
  input: {
    type: "custom" as const,
    label: "Input configuration",
    render: ({ value, onChange }: { value: import("./types").InputBinding; onChange: (value: import("./types").InputBinding) => void }) =>
      <InputInspector value={value} onChange={onChange} kind={kind} />,
  },
  style: styleField,
});

const templateTextField = (label: string, instructions: string, multiline = false, placeholder?: string) => ({
  type: "custom" as const,
  label,
  render: ({ value, onChange }: { value?: string; onChange: (value: string) => void }) =>
    <TemplateStringInspector value={value} onChange={onChange} instructions={instructions} ariaLabel={label} multiline={multiline} placeholder={placeholder} />,
});

const ParagraphPreview = ({ segments, text, style }: TemplateComponents["Paragraph"]) => {
  const { inputDefinitions } = useTemplateBuilder();
  return <p style={browserStyle(style)}>{paragraphPreview(segments ?? (text ? [{ text }] : undefined), inputDefinitions)}</p>;
};

const TitlePreview = ({ title, style, tag: Tag }: { title?: string; style?: import("./types").BlockStyle | string; tag: "h1" | "h2" }) => {
  const { inputDefinitions } = useTemplateBuilder();
  const text = templateTextPreview(title, inputDefinitions);
  return <Tag style={browserStyle(style)}>{text.trim() ? text : <span className="template-builder-placeholder">{Tag === "h1" ? "Document title" : "Section heading"}</span>}</Tag>;
};

const TablePreview = ({ headers, rows, style }: TemplateComponents["Table"]) => {
  const { inputDefinitions } = useTemplateBuilder();
  const columns = String(headers ?? "").split("|").map((item) => item.trim()).filter(Boolean);
  const cells = String(rows ?? "").split("\n").filter(Boolean).map((row) => row.split("|").map((item) => item.trim()));
  const textStyle = browserTextStyle(style);
  return <table className="puck-table" style={browserStyle(style)}><thead><tr>{columns.map((column, index) => <th key={`${column}-${index}`} style={textStyle}>{templateTextPreview(column, inputDefinitions)}</th>)}</tr></thead><tbody>{cells.map((row, rowIndex) => <tr key={rowIndex}>{columns.map((_, columnIndex) => <td key={columnIndex} style={textStyle}>{templateTextPreview(row[columnIndex] ?? "", inputDefinitions)}</td>)}</tr>)}</tbody></table>;
};

const InputBlock = ({ input, style, kind }: { input: import("./types").InputBinding; style?: import("./types").BlockStyle | string; kind: InputKind }) =>
  <InputPreview input={input} style={style} kind={kind} />;

export const createTemplateBuilderConfig = (
  ensureInsertedInput?: (actor: import("./types").InputActor, inputId: string, kind: InputKind) => void,
): Config<TemplateComponents> => {
  const inputComponent = (kind: InputKind, actor: import("./types").InputActor, inputId: string): Pick<InputComponentConfig, "fields" | "defaultProps" | "resolveData"> => ({
    fields: inputFields(kind),
    defaultProps: { input: { actor, inputId } },
    // Puck 0.23 resolves an inserted block after assigning its block ID.  Bind the
    // definition here so the inspector and preview never observe a transient ID.
    resolveData: (data, { trigger }) => {
      if (trigger !== "insert") return {};
      const generatedId = generatedInputId(data.props.id);
      if (!generatedId) return {};
      ensureInsertedInput?.(actor, generatedId, kind);
      return { props: { input: { actor, inputId: generatedId } } };
    },
  });
  return {
  // The root is an explicit Puck drop zone so its allow-list is applied during
  // drag targeting. This rejects invalid root drops without an editor error.
  root: {
    render: ({ puck }) => <>{puck.renderDropZone({
      // Puck 0.23 stores/renders root content in this canonical zone.  "root"
      // targets root:root and leaves the normal `content` array invisible.
      zone: templateBuilderRootZone,
      allow: templateBuilderRootAllowedBlocks,
      className: "template-builder-root",
    })}</>,
  },
  categories: {
    document: { title: "Document", components: ["DocumentTitle", "SectionHeading", "Paragraph", "Link", "Table"] },
    layout: { title: "Layout", components: ["Page", "Divider"] },
    form: { title: "Template inputs", components: ["TextInput", "TextArea", "DateInput", "DateTimeInput", "Checkbox", "CheckboxGroup", "RadioInput"] },
  },
  components: {
    Page: { label: "Page",
      fields: { content: { type: "slot", allow: ["DocumentTitle", "SectionHeading", "Paragraph", "Link", "TextInput", "TextArea", "DateInput", "DateTimeInput", "Checkbox", "CheckboxGroup", "RadioInput", "Divider", "Table"] }, style: styleField },
      defaultProps: { content: [] },
      render: ({ content: Content, style }) => <section
        className="template-builder-page"
        style={{
          ...browserStyle(style),
          boxSizing: "border-box",
          width: "210mm",
          minWidth: "210mm",
          maxWidth: "210mm",
          height: "297mm",
          minHeight: "297mm",
          maxHeight: "297mm",
          background: 'white',
          boxShadow: '4px 4px 10px rgba(0, 0, 0, 0.2)'
        }}
      >{Content()}</section>,
    },
    DocumentTitle: { label: "Document title", fields: { title: templateTextField("Document title", "Write the title, then insert approved document or form values at the cursor.", false, "Document title"), style: styleField }, defaultProps: { title: "" }, render: ({ title, style }) => <TitlePreview tag="h1" title={title} style={style} /> },
    AutomaticValue: { label: "Automatic value", fields: { field: { type: "select", options: automaticKeys }, style: styleField }, defaultProps: { field: "resident.name" }, render: ({ field, style }) => <p style={browserStyle(style)}>{sampleValueFor(field)}</p> },
    InlineAutomaticValue: { label: "Inline automatic value", fields: { field: { type: "select", options: automaticKeys }, style: styleField }, defaultProps: { field: "resident.name" }, render: ({ field, style }) => <span style={browserStyle(style)}>{sampleValueFor(field)}</span> },
    SectionHeading: { label: "Section heading", fields: { title: templateTextField("Section heading", "Write the heading, then insert approved document or form values at the cursor."), style: styleField }, defaultProps: { title: "Section heading" }, render: ({ title, style }) => <TitlePreview tag="h2" title={title} style={style} /> },
    Paragraph: { label: "Paragraph", fields: { segments: { type: "custom", label: "Paragraph content", render: ({ value, onChange }: { value?: import("./types").ParagraphSegment[]; onChange: (value: import("./types").ParagraphSegment[]) => void }) => <ParagraphInspector value={value} onChange={onChange} /> }, style: styleField }, defaultProps: { segments: [{ text: "Add explanatory text here." }] }, render: ParagraphPreview },
    Link: { label: "Link", fields: { text: { type: "text" }, url: { type: "text" }, style: styleField }, defaultProps: { text: "Open link", url: "https://www.health.gov.au/" }, render: ({ text, url, style }) => <a href={url} target="_blank" rel="noreferrer" style={browserStyle(style)}>{text}</a> },
    TextInput: { label: "Text input", ...inputComponent("text", "family", "__new_text"), render: ({ input, style }) => <InputBlock input={input} style={style} kind="text" /> },
    TextArea: { label: "Text area", ...inputComponent("textarea", "family", "__new_textarea"), render: ({ input, style }) => <InputBlock input={input} style={style} kind="textarea" /> },
    DateInput: { label: "Date input", ...inputComponent("date", "family", "__new_date"), render: ({ input, style }) => <InputBlock input={input} style={style} kind="date" /> },
    DateTimeInput: { label: "Date & time input", ...inputComponent("datetime", "family", "__new_datetime"), render: ({ input, style }) => <InputBlock input={input} style={style} kind="datetime" /> },
    Checkbox: { label: "Checkbox", ...inputComponent("checkbox", "employee", "__new_checkbox"), render: ({ input, style }) => <InputBlock input={input} style={style} kind="checkbox" /> },
    CheckboxGroup: { label: "Checkbox group", ...inputComponent("choice", "family", "__new_choice"), render: ({ input, style }) => <InputBlock input={input} style={style} kind="choice" /> },
    RadioInput: { label: "Radio", ...inputComponent("radio", "family", "__new_radio"), render: ({ input, style }) => <InputBlock input={input} style={style} kind="radio" /> },
    // Keep a real props object and a hit target. An empty-props <hr> is easy for
    // Puck to store without props, and a click then walks an undefined slot child.
    Divider: {
      label: "Divider",
      fields: { style: styleField },
      defaultProps: { style: writeStyle({ marginTop: 8, marginBottom: 8 }) },
      render: ({ style }) => <div className="template-builder-divider" style={browserStyle(style)}><hr className="puck-divider" /></div>,
    },
    Table: {
      label: "Table",
      fields: {
        headers: templateTextField("Column headers", "Separate columns with |. Insert automatic values at the cursor."),
        rows: templateTextField("Rows", "One row per line, separate cells with |. Insert automatic values at the cursor.", true),
        style: styleField,
      },
      defaultProps: { headers: "Question | Response", rows: "Consent given | \nSignature received | " },
      render: TablePreview,
    },
  },
  };
};

export const blockKind = inputKindForBlock;
